import React, { useState, useEffect, useLayoutEffect, useRef, useMemo, useCallback } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { useBreadcrumb } from '../../contexts/BreadcrumbContext';
import { fetchPracticeSummary } from '../../store/slices/practiceSlice';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUserInjured } from '@fortawesome/free-solid-svg-icons';
import PatientManagement from '../PracticeManagement/PatientManagement/PatientManagement';
import { buildProgramRibbonItems } from '../../utils/careProgramUtils';
import { formatDateOnlyDash } from '../../utils/dateOnlyUtils';
import '../PracticeManagement/PracticeDashboard/PracticeDashboard.css';
import './CaregiverDashboard.css';

const CaregiverDashboard = () => {
  const { user } = useSelector((state) => state.auth);
  const practiceId = user?.practice_id;
  const { currentPractice } = useSelector((state) => state.practice ?? {});

  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { updateBreadcrumbs, clearBreadcrumbs } = useBreadcrumb();

  const [practiceData, setPracticeData] = useState(() => {
    if (!currentPractice) return null;
    return String(currentPractice.id) === String(practiceId) ? currentPractice : null;
  });
  const [loading, setLoading] = useState(() => {
    if (!currentPractice) return true;
    return String(currentPractice.id) !== String(practiceId);
  });
  const tabContentRef = useRef(null);

  const tabs = useMemo(
    () => [{ id: 'patient', label: 'Patients', icon: faUserInjured }],
    []
  );

  const getTabFromSearch = useCallback((search) => {
    const params = new URLSearchParams(search);
    const requested = params.get('tab');
    return tabs.some((tab) => tab.id === requested) ? requested : 'patient';
  }, [tabs]);

  const [searchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState(() => getTabFromSearch(location.search));

  const selectedProgramId = useMemo(() => {
    const programParam = String(searchParams.get('program') || '').trim().toLowerCase();
    return activeTab === 'patient' && programParam ? programParam : '';
  }, [searchParams, activeTab]);

  const normalizeProgramKey = (name) => {
    if (!name) return '';
    return String(name).trim().toLowerCase().replace(/\s+/g, '-');
  };

  const selectedProgramRange = useMemo(() => {
    if (!selectedProgramId || !Array.isArray(practiceData?.enrolled_programs)) return null;
    return practiceData.enrolled_programs
      .map((program) => {
        if (!program) return null;
        const programName = typeof program === 'string'
          ? program
          : String(program.short_name || program.id || program.full_name || program.program || program.label || '').trim();
        if (!programName) return null;
        return {
          key: normalizeProgramKey(programName),
          name: programName,
          start_date: typeof program === 'string'
            ? null
            : program.start_date || program.rpm_start_date || program.startDate || null,
          end_date: typeof program === 'string'
            ? null
            : program.end_date || program.rpm_end_date || program.endDate || null
        };
      })
      .filter(Boolean)
      .find((range) => range.key === selectedProgramId) || null;
  }, [selectedProgramId, practiceData?.enrolled_programs]);

  useLayoutEffect(() => {
    const params = new URLSearchParams(location.search);
    const urlTab = params.get('tab');

    if (urlTab && tabs.some((tab) => tab.id === urlTab)) {
      setActiveTab((prev) => (prev === urlTab ? prev : urlTab));
      return;
    }

    if (!urlTab) {
      params.set('tab', 'patient');
      navigate({ pathname: location.pathname, search: params.toString() }, { replace: true });
    }
  }, [location.search, location.pathname, navigate, tabs]);

  useEffect(() => {
    const loadPracticeData = async () => {
      if (!practiceId) {
        setLoading(false);
        return;
      }
      try {
        if (currentPractice && String(currentPractice.id) === String(practiceId)) {
          setPracticeData(currentPractice);
          setLoading(false);
          return;
        }

        setLoading(true);
        const result = await dispatch(fetchPracticeSummary(practiceId));
        if (fetchPracticeSummary.fulfilled.match(result)) {
          setPracticeData(result.payload.data);
        } else {
          console.error('Failed to load practice:', result.payload);
        }
      } catch (error) {
        console.error('Error loading practice:', error);
      } finally {
        setLoading(false);
      }
    };

    if (practiceId) {
      loadPracticeData();
    }
  }, [practiceId, dispatch, currentPractice]);

  useEffect(() => {
    if (!practiceId) return undefined;
    const refreshCounts = async (event) => {
      const changedId = event?.detail?.practiceId;
      if (changedId != null && String(changedId) !== String(practiceId)) return;
      const sum = await dispatch(fetchPracticeSummary(practiceId));
      if (fetchPracticeSummary.fulfilled.match(sum)) {
        setPracticeData(sum.payload.data);
      }
    };
    window.addEventListener('evitals:practice-patients-changed', refreshCounts);
    return () => window.removeEventListener('evitals:practice-patients-changed', refreshCounts);
  }, [practiceId, dispatch]);

  useEffect(() => {
    if (practiceData) {
      const currentTab = tabs.find(tab => tab.id === activeTab);
      
      if (activeTab === 'dashboard') {
        updateBreadcrumbs([]);
      } else {
        const baseCrumbs = [
          { label: practiceData.practice_name, path: '/dashboard?tab=dashboard', active: false },
          { label: currentTab?.label || 'Practice', path: null, active: true }
        ];
        updateBreadcrumbs(baseCrumbs);
      }
    }
  }, [practiceData, activeTab, tabs, updateBreadcrumbs]);

  useEffect(() => () => clearBreadcrumbs(), [clearBreadcrumbs]);

  const handleTabChange = (tabId) => {
    setActiveTab(tabId);
    const params = new URLSearchParams(location.search);
    params.set('tab', tabId);
    params.delete('viewId');
    if (tabId !== 'patient') {
      params.delete('program');
    }
    navigate({ pathname: location.pathname, search: params.toString() }, { replace: true });
  };

  const formatBillingPeriodDate = (iso) => {
    if (!iso) return 'N/A';
    return formatDateOnlyDash(iso) || 'N/A';
  };

  const renderProgramSummary = () => {
    const enrolled = Array.isArray(practiceData?.enrolled_programs) ? practiceData.enrolled_programs : [];
    if (enrolled.length === 0) {
      return (
        <div className="practice-dashboard-program-ribbon">
          <span className="program-ribbon-text text-muted">No care programs enrolled</span>
        </div>
      );
    }

    const programs = buildProgramRibbonItems(enrolled, practiceData.program_counts || {});

    return (
      <div className="practice-dashboard-program-ribbon">
        {programs.map((prog) => (
          <div key={prog.id} className="program-ribbon-item">
            <FontAwesomeIcon icon={prog.icon} className="program-ribbon-icon" style={{ color: prog.color }} />
            <span className="program-ribbon-text">
              <span className="program-ribbon-count">{prog.count}</span>{' '}
              {prog.displayName || prog.full_name || prog.label}
            </span>
          </div>
        ))}
      </div>
    );
  };

  const renderProgramDates = () => {
    if (!selectedProgramId || !selectedProgramRange) {
      return null;
    }

    return (
      <div className="practice-dashboard-billing-periods">
        <div key={selectedProgramRange.key} className="practice-dashboard-period-row">
          <span className="practice-dashboard-period-name">{selectedProgramRange.name}</span>
          <span className="practice-dashboard-period-value">
            {formatBillingPeriodDate(selectedProgramRange.start_date)} - {formatBillingPeriodDate(selectedProgramRange.end_date)}
          </span>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="practice-dashboard-loading-container app-page-loader">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  if (!practiceId) {
    return (
      <div className="alert alert-warning" role="alert">
        No practice associated with this caregiver account.
      </div>
    );
  }

  if (!practiceData) {
    return (
      <div className="alert alert-danger" role="alert">
        Practice not found!
      </div>
    );
  }

  return (
    <div className="practice-dashboard caregiver-dashboard-ermp">
      <div className="practice-dashboard-single-card">
        <section className="practice-dashboard-card-header">
          <div className="practice-dashboard-header-main">
            <div className="practice-dashboard-header-left">
              <h2 className="practice-dashboard-title">{practiceData.practice_name}</h2>
              {renderProgramSummary()}
            </div>
            {activeTab === 'patient' && selectedProgramRange && (
              <div className="practice-dashboard-header-right">
                <div className="practice-dashboard-billing-info">
                  <span className="billing-label">Billing Dates</span>
                  {renderProgramDates()}
                </div>
              </div>
            )}
          </div>
        </section>


        <section className="practice-dashboard-card-body" ref={tabContentRef}>
          {activeTab === 'patient' && (
            <PatientManagement
              practiceId={practiceId}
              embeddedInCaregiverDashboard
              isDashboardView
              dashboardTabKey="patient"
            />
          )}
        </section>
      </div>
    </div>
  );
};

export default CaregiverDashboard;
